import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

type WalletEditModalProps = {
  walletId: number
  initialName?: string
  initialDashboard?: boolean
  initialNotify?: boolean
  onClose: () => void
}

export function WalletEditModal({ walletId, initialName = '', initialDashboard = true, initialNotify = true, onClose }: WalletEditModalProps) {
  const { t } = useTranslation('wallets')
  const queryClient = useQueryClient()
  const [name, setName] = useState(initialName)
  const [dashboard, setDashboard] = useState(initialDashboard)
  const [notify, setNotify] = useState(initialNotify)

  const mutation = useMutation({
    mutationFn: async (payload: { wallet: { name: string; dashboard: boolean; notify: boolean } }) => {
      const { data, error } = await apiClient.PATCH('/api/v2/wallets/{id}', {
        params: { path: { id: walletId } },
        body: payload
      })
      if (error) throw new Error('Failed to update wallet')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', String(walletId)] })
      queryClient.invalidateQueries({ queryKey: ['wallets'] })
      onClose()
    }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({
      wallet: {
        name,
        dashboard,
        notify
      }
    })
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" role="dialog" aria-labelledby="wallet-edit-title" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label={t('actions.cancel')}>
          ×
        </button>
        <h2 id="wallet-edit-title" style={{ marginTop: 0, marginBottom: 24, fontSize: 18, fontWeight: 600 }}>
          {t('actions.editWallet')}
        </h2>
        <form onSubmit={handleSubmit} data-testid="edit-wallet-modal">
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label htmlFor="walletName">{t('fields.name')}</label>
            <input
              id="walletName"
              name="name"
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('fields.namePlaceholder')}
            />
          </div>
          <div className="form-group checkbox" style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                name="dashboard"
                checked={dashboard}
                onChange={(e) => setDashboard(e.target.checked)}
              />
              {t('fields.dashboard')}
            </label>
          </div>
          <div className="form-group checkbox" style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                name="notify"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              {t('fields.notify')}
            </label>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" data-testid="btn-save-wallet" disabled={mutation.isPending}>
              {mutation.isPending ? t('actions.saving') : t('actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
